import { useReportContext } from "../../../report-context";

import { useAuth } from "@/context/auth-context";
import { ToastType } from "@/enums";
import { useCreateReportColumn, usePatchReportColumn } from "@/queries";
import { ReportColumn } from "@/types/evaluate";
import { displayToast } from "@/utils/toast";

interface UseColumnMutationsProps {
  type?: "create" | "update";
}

const useColumnMutations = ({ type }: UseColumnMutationsProps) => {
  const auth = useAuth();
  const userToken = auth!.userToken!;
  const reportContext = useReportContext();
  const { selectedColumn, reportId } = reportContext;

  const { mutateAsync: createColumn, isLoading: isCreatingColumn } = useCreateReportColumn(
    userToken,
    reportId,
    {
      onSuccess: response => {
        // Update the selected column with the new ID from the backend
        if (selectedColumn && response?.report_column) {
          reportContext.setSelectedColumn({
            ...selectedColumn,
            ...response.report_column,
            id: response.report_column.id,
          });
        }
        // Clear draft after successful creation
        reportContext.clearPersistedDraftColumn();
        displayToast(
          `Column "${selectedColumn?.name || "New Step"}" created successfully`,
          ToastType.success,
        );
      },
      onError: error => {
        displayToast(
          typeof error === "string" ? error : "Failed to create column",
          ToastType.error,
        );
      },
    },
  );

  const { mutateAsync: patchColumn, isLoading: isUpdatingColumn } = usePatchReportColumn(
    userToken,
    reportId,
    {
      onSuccess: () => {
        displayToast(
          `Column "${selectedColumn?.name || "Column"}" updated successfully`,
          ToastType.success,
        );
      },
      onError: error => {
        displayToast(
          typeof error === "string" ? error : "Failed to update column",
          ToastType.error,
        );
      },
    },
  );

  const saveColumn = async (updatedColumn: ReportColumn): Promise<void> => {
    if (type === "create") {
      await createColumn(updatedColumn);
    } else if (type === "update" && selectedColumn?.id) {
      await patchColumn(updatedColumn);
    } else {
      displayToast("Cannot update column without ID", ToastType.error);
    }
  };

  return {
    saveColumn,
    isLoading: isCreatingColumn || isUpdatingColumn,
  };
};

export default useColumnMutations;
